import type { Enduser } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Label } from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { FieldDescription } from "../../FieldDescription";
import type { TellescopeFormFieldBaseProps, TellescopeFieldResponse } from "../../types";
import {
  EligibilityFieldContent,
  EligibilityStaffPanel,
  STEDI_ELIGIBILITY_VENDOR,
  useEligibilityField,
} from "../EligibilityField";

export interface StediEligibilityFieldProps extends TellescopeFormFieldBaseProps<"Stedi Eligibility"> {
  enduserId: string;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  priorResponses: TellescopeFieldResponse[];
  sessionType?: "enduser" | "user";
  /** Links the credits this check consumes to the response it was run for. */
  formResponseId?: string;
}

export const StediEligibilityField = ({
  title,
  description,
  htmlDescription,
  value,
  onChange,
  options,
  enduserId,
  enduser,
  priorResponses,
  className,
  error,
  sessionType = "enduser",
  formResponseId,
}: StediEligibilityFieldProps) => {
  const { phase, displayStatus, payerName, payerId, memberId, errorMessage, responseErrors, isChecking, triggerCheck } =
    useEligibilityField<"Stedi Eligibility">({
      vendor: STEDI_ELIGIBILITY_VENDOR,
      value,
      onChange,
      options,
      enduserId,
      enduser,
      priorResponses,
      sessionType,
      formResponseId,
    });

  return (
    <div className={cn("space-y-1", className)}>
      {!!title && <Label required={false}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      {sessionType === "user" ? (
        <EligibilityStaffPanel
          serviceCode={options?.stediServiceCode}
          npi={options?.stediNPI}
          payerId={payerId}
          memberId={memberId}
          isChecking={isChecking}
          errorMessage={errorMessage}
          responseErrors={responseErrors}
          value={value}
          onCheck={triggerCheck}
        />
      ) : (
        <EligibilityFieldContent phase={phase} displayStatus={displayStatus} payerName={payerName} />
      )}
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
